import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';

const TYPE_LABEL = {
  stage_1_audit: 'Stage 1 Audit',
  stage_2_audit: 'Stage 2 Audit',
  certification:  'Certificate',
};
const TYPE_COLOR = {
  stage_1_audit: { bg: '#fff3cd', color: '#856404' },
  stage_2_audit: { bg: '#f3f0ff', color: '#6f42c1' },
  certification:  { bg: '#d1fae5', color: '#065f46' },
};
const STATUS_COLOR = {
  active:    { bg: '#d1fae5', color: '#065f46' },
  expired:   { bg: '#fee2e2', color: '#991b1b' },
  revoked:   { bg: '#fee2e2', color: '#991b1b' },
  suspended: { bg: '#fff3cd', color: '#856404' },
};

export default function CertificatesManager() {
  const navigate  = useNavigate();
  const userRole  = JSON.parse(localStorage.getItem("user") || "{}").role;
  const canDelete  = userRole === "super_admin" || userRole === "third_party_admin";
  const canRelease = userRole === "super_admin" || userRole === "third_party_admin" || userRole === "certification_body";
  const isClient   = userRole === "client";
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');
  const [search, setSearch]       = useState('');
  const [deleteId, setDeleteId]   = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [releasing, setReleasing] = useState(null);
  const [error, setError]         = useState('');

  useEffect(() => { fetchRecords(); }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get('/certifications/certifications/');
      setRecords(Array.isArray(res.data) ? res.data : []);
    } catch { setRecords([]); }
    finally { setLoading(false); }
  };

  const handleRelease = async (id, currentlyReleased) => {
    setReleasing(id);
    try {
      const endpoint = currentlyReleased
        ? `/certifications/certifications/${id}/unrelease`
        : `/certifications/certifications/${id}/release`;
      await axiosClient.post(endpoint);
      fetchRecords();
    } catch (err) {
      setError('Release failed: ' + (err.response?.data?.detail || err.message));
    } finally { setReleasing(null); }
  };

  const handleDownloadPdf = async (id, certNumber) => {
    try {
      const res = await axiosClient.get(`/certifications/certifications/${id}/download-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `ISO_Certificate_${certNumber || id}.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError('PDF download failed'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axiosClient.delete(`/certifications/certifications/${deleteId}/`);
      setDeleteId(null);
      fetchRecords();
    } catch (err) {
      setError('Delete failed: ' + (err.response?.data?.detail || err.message));
      setDeleteId(null);
    } finally { setDeleting(false); }
  };

  const filtered = records.filter(r => {
    const matchType = filter === 'all' || r.record_type === filter;
    const q = search.toLowerCase();
    const matchSearch = !q
      || (r.company_name || '').toLowerCase().includes(q)
      || (r.iso_standard || '').toLowerCase().includes(q)
      || (r.certificate_number || '').toLowerCase().includes(q)
      || (r.auditor_name || '').toLowerCase().includes(q);
    return matchType && matchSearch;
  });

  // KPIs
  const total      = records.length;
  const certs      = records.filter(r => r.record_type === 'certification').length;
  const stage1     = records.filter(r => r.record_type === 'stage_1_audit').length;
  const stage2     = records.filter(r => r.record_type === 'stage_2_audit').length;
  const active     = records.filter(r => r.status === 'active').length;
  const expiring   = records.filter(r => {
    if (!r.expiry_date) return false;
    const days = (new Date(r.expiry_date) - new Date()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 90;
  }).length;

  const kpis = [
    { label: 'Total Records', value: total, color: '#007bff' },
    { label: 'Certificates Issued', value: certs, color: '#28a745' },
    { label: 'Stage 1 Audits', value: stage1, color: '#fd7e14' },
    { label: 'Stage 2 Audits', value: stage2, color: '#6f42c1' },
    { label: 'Active', value: active, color: '#20c997' },
    { label: 'Expiring (90 days)', value: expiring, color: '#dc3545' },
  ];

  const FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'stage_1_audit', label: 'Stage 1 Audits' },
    { value: 'stage_2_audit', label: 'Stage 2 Audits' },
    { value: 'certification', label: 'Certificates' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Certificate Manager</h1>
          <p style={{ margin: '4px 0 0', color: '#666' }}>All audit records and issued certificates</p>
        </div>
        {canDelete && (
          <button
            onClick={() => navigate('/iso-certificates')}
            style={{ padding: '10px 20px', background: '#007bff', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
            + Log Audit / Issue Cert
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: '#f8d7da', border: '1px solid #dc3545', borderRadius: '4px', padding: '12px', marginBottom: '16px', color: '#842029' }}>
          {error}
        </div>
      )}

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: `3px solid ${k.color}` }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: k.color }}>{k.value}</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filters + Search */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {FILTERS.map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid #dee2e6', cursor: 'pointer', fontWeight: filter === f.value ? '700' : '400', background: filter === f.value ? '#007bff' : 'white', color: filter === f.value ? 'white' : '#333', fontSize: '13px' }}>
              {f.label}
            </button>
          ))}
        </div>
        <input
          placeholder="Search company, standard, cert #, auditor..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '240px', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '6px', fontSize: '14px' }}
        />
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #dee2e6', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>
            No records found.{' '}
            <span style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/iso-certificates')}>
              Log an audit or issue a certificate
            </span>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  {['Type', 'Company', 'ISO Standard', 'Date', 'Auditor', 'Certificate #', 'Expiry', 'Status',
                    ...(canRelease ? ['Released'] : []),
                    ...(canDelete ? [''] : []),
                    ...(isClient ? [''] : [])
                  ].map((h, i) => (
                    <th key={i} style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const tc = TYPE_COLOR[r.record_type] || TYPE_COLOR.certification;
                  const sc = STATUS_COLOR[r.status] || STATUS_COLOR.expired;
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '12px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', background: tc.bg, color: tc.color }}>
                          {TYPE_LABEL[r.record_type] || r.record_type}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>{r.company_name || r.company_id}</td>
                      <td style={{ padding: '12px', fontSize: '13px', fontWeight: '600' }}>{r.iso_standard}</td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        {r.audit_date
                          ? new Date(r.audit_date).toLocaleDateString()
                          : r.issue_date ? new Date(r.issue_date).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>{r.auditor_name || '—'}</td>
                      <td style={{ padding: '12px', fontSize: '12px', fontFamily: 'monospace', color: r.certificate_number ? '#065f46' : '#999' }}>
                        {r.certificate_number || '—'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        {r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', background: sc.bg, color: sc.color }}>
                          {r.status}
                        </span>
                      </td>
                      {canRelease && r.record_type === 'certification' && (
                        <td style={{ padding: '12px' }}>
                          <button
                            onClick={() => handleRelease(r.id, r.released)}
                            disabled={releasing === r.id}
                            style={{
                              padding: '4px 10px', fontSize: '12px', border: 'none', borderRadius: '4px', cursor: 'pointer',
                              background: r.released ? '#28a745' : '#6c757d', color: 'white', fontWeight: '600'
                            }}>
                            {releasing === r.id ? '...' : r.released ? '✓ Released' : 'Release'}
                          </button>
                        </td>
                      )}
                      {canRelease && r.record_type !== 'certification' && <td />}
                      {canDelete && (
                        <td style={{ padding: '12px' }}>
                          <button onClick={() => setDeleteId(r.id)}
                            style={{ padding: '4px 10px', fontSize: '12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                            Delete
                          </button>
                        </td>
                      )}
                      {isClient && r.record_type === 'certification' && r.released && (
                        <td style={{ padding: '12px' }}>
                          <button
                            onClick={() => handleDownloadPdf(r.id, r.certificate_number)}
                            style={{ padding: '4px 10px', fontSize: '12px', background: '#1e40af', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                            Download PDF
                          </button>
                        </td>
                      )}
                      {isClient && (r.record_type !== 'certification' || !r.released) && <td />}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ margin: '0 0 12px', color: '#dc3545' }}>Delete Record?</h3>
            <p style={{ margin: '0 0 20px', color: '#555' }}>This will permanently delete this audit/certificate record. This cannot be undone.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteId(null)} disabled={deleting}
                style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: deleting ? 'not-allowed' : 'pointer' }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
